import request from 'axios';
import { getClientAuthHeader } from '../../client/token';

// Reports client-side errors to the server, which console.errors them into
// the journald-backed admin error log with the user's context attached.
// Capped per page load so a render loop can't flood the server.

const MAX_REPORTS_PER_SESSION = 10;
let reported = 0;
const seen = {};

export const reportClientError = (message, stack = undefined) => {
    try {
        if (typeof window === 'undefined' || reported >= MAX_REPORTS_PER_SESSION) {
            return;
        }

        message = String(message || '').slice(0, 4000);
        if (!message || seen[message]) {
            return;
        }

        seen[message] = true;
        reported += 1;
        request.post('/api/v1/log/error', {
            message,
            stack: stack ? String(stack).slice(0, 8000) : undefined,
            url: window.location.href,
        }, getClientAuthHeader()).catch(() => {});

    } catch (error) {
        // Reporting must never throw.
    }
};

// After a deploy the hashed chunks of the previous build are gone, so a tab that was open across it fails to
// import them (the server answers with the HTML fallback). A reload fetches the new build; once per minute at
// most, so a genuinely broken chunk can't cause a reload loop.
const STALE_CHUNK = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type/i;
const RELOAD_KEY = 'staleChunkReloadAt';

const reloadOnStaleChunk = (message) => {
    try {
        if (!STALE_CHUNK.test(String(message || ''))) {
            return false;
        }

        const last = parseInt(window.sessionStorage.getItem(RELOAD_KEY), 10) || 0;
        if (Date.now() - last < 60000) {
            return false;
        }

        window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
        return true;

    } catch (error) {
        return false;
    }
};

export const installGlobalErrorReporting = () => {
    if (typeof window === 'undefined') {
        return;
    }

    window.addEventListener('vite:preloadError', (evt) => {
        if (reloadOnStaleChunk(evt.payload && evt.payload.message || 'Failed to fetch dynamically imported module')) {
            evt.preventDefault();
        }
    });

    window.addEventListener('error', (evt) => {
        if (reloadOnStaleChunk(evt.message)) {
            return;
        }

        reportClientError(evt.message, evt.error && evt.error.stack);
    });

    window.addEventListener('unhandledrejection', (evt) => {
        const reason = evt.reason;
        if (reloadOnStaleChunk(reason && reason.message)) {
            return;
        }

        reportClientError((reason && reason.message) || String(reason), reason && reason.stack);
    });
};
